import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import KarmaMatrix from './KarmaMatrix';
import GridCellsRenderer from './GridCellsRenderer';
import EnergyCalculator from './EnergyCalculator';
import { evaluateFormula, sumKarmaResults } from '../utils/karmaCalc';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Renders extra "variant" diagrams below the main matrix on the same page.
 * Each variant has its own independent config (letters/positions/etc.) but
 * uses the SAME birth date input from the parent page.
 *
 * Props:
 *  - parentPage: 'main' | 'student' | 'user_<slug>'
 *  - variants:   [{ id, title }] from parent config
 *  - layout:     'stacked' | 'tabs'
 *  - results, day, month, year — shared computation output
 *  - authHeaders — optional auth headers (student page)
 */
const VariantsSection = ({
  parentPage, variants, layout = 'stacked',
  results, day, month, year,
  compatEnabled = false, results2 = null,
  day2 = null, month2 = null, year2 = null,
  birthDate = null, birthDate2 = null,
  sumEnabled = false, sumReduce22 = true,
  authHeaders,
}) => {
  const [configs, setConfigs] = useState({}); // { variantId: config }
  const [activeTab, setActiveTab] = useState(variants?.[0]?.id || '');
  // Per-variant connection-group toggle overrides: { variantId: { groupId: bool } }
  const [groupOverrides, setGroupOverrides] = useState({});

  // Σ sums for variants — same rule as the main page: only computed when
  // compat mode is on AND the parent has enabled sum display.
  const sumResults = useMemo(() => {
    if (!compatEnabled || !sumEnabled || !results || !results2) return null;
    return sumKarmaResults(results, results2, { reduce: sumReduce22 });
  }, [compatEnabled, sumEnabled, sumReduce22, results, results2]);

  useEffect(() => {
    if (!Array.isArray(variants) || variants.length === 0) return;
    let cancelled = false;
    (async () => {
      const loaded = {};
      for (const v of variants) {
        try {
          const res = await axios.get(`${API}/config`, {
            params: { page: `${parentPage}_var_${v.id}` },
            headers: authHeaders || {},
          });
          loaded[v.id] = res.data;
        } catch (e) {
          // Skip variants we can't load (probably deleted or non-public)
        }
      }
      if (!cancelled) setConfigs(loaded);
    })();
    return () => { cancelled = true; };
  }, [parentPage, variants, authHeaders]);

  if (!results || !Array.isArray(variants) || variants.length === 0) return null;

  const isGroupEnabled = (variantId, g) => {
    const overrides = groupOverrides[variantId] || {};
    return overrides[g.id] !== undefined ? overrides[g.id] : g.enabled;
  };

  const toggleVariantGroup = (variantId, groupId) => {
    const c = configs[variantId];
    const grp = (c?.connection_groups || []).find(g => g.id === groupId);
    const overrides = groupOverrides[variantId] || {};
    const currentlyEnabled = overrides[groupId] !== undefined
      ? overrides[groupId]
      : (grp ? grp.enabled : true);
    setGroupOverrides({
      ...groupOverrides,
      [variantId]: { ...overrides, [groupId]: !currentlyEnabled },
    });
  };

  // Build (effectiveConnections, connectionColorMap) for a variant using its
  // own connection_groups + toggles — completely independent from the parent.
  const buildEffectiveConnections = (v, c) => {
    const base = c.connections || [];
    const groups = c.connection_groups || [];
    const fromGroups = [];
    const colorMap = {};
    for (const g of groups) {
      if (!isGroupEnabled(v.id, g)) continue;
      const color = g.color || '#a855f7';
      for (const conn of (g.connections || [])) {
        if (!Array.isArray(conn) || conn.length !== 2) continue;
        fromGroups.push(conn);
        const key = conn[0] < conn[1] ? `${conn[0]}|${conn[1]}` : `${conn[1]}|${conn[0]}`;
        colorMap[key] = color;
      }
    }
    // De-dup
    const seen = new Set();
    const out = [];
    for (const conn of [...base, ...fromGroups]) {
      if (!Array.isArray(conn) || conn.length !== 2) continue;
      const key = conn[0] < conn[1] ? `${conn[0]}|${conn[1]}` : `${conn[1]}|${conn[0]}`;
      if (!seen.has(key)) { seen.add(key); out.push(conn); }
    }
    return { effective: out, colorMap };
  };

  const renderOne = (v) => {
    const c = configs[v.id];
    if (!c) {
      return (
        <div key={v.id} style={{ padding: 20, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
          Yüklənir…
        </div>
      );
    }
    const variantLayers = c.background_layers || [];
    const groups = c.connection_groups || [];
    const { effective: effectiveConnections, colorMap: variantColorMap } = buildEffectiveConnections(v, c);
    // Merge admin-defined per-connection color map with the group color map
    const mergedColorMap = { ...(c.connection_color_map || {}), ...variantColorMap };
    // Per-variant width (px) — admin can set 300-1000; default 800
    const maxWidthPx = Math.max(300, Math.min(1000, parseInt(c.max_width, 10) || 800));
    const boxWidth = `min(${maxWidthPx}px, 92vw)`;
    const titleScale = Math.max(0.5, Math.min(2.5, parseFloat(c.title_scale) || 1));
    // Where the groups toggle bar sits relative to matrix + grid
    const groupsPos = ['above', 'below-matrix', 'below-grid'].includes(c.groups_position)
      ? c.groups_position
      : 'below-grid';

    const groupsBar = groups.length > 0 ? (
      <div
        className="conn-group-toggles"
        data-testid={`variant-groups-${v.id}`}
        style={{ marginTop: 12 }}
      >
        {groups.map((g) => {
          const enabled = isGroupEnabled(v.id, g);
          const color = g.color || '#a855f7';
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => toggleVariantGroup(v.id, g.id)}
              className={`conn-group-toggle ${enabled ? 'conn-group-toggle-on' : ''}`}
              data-testid={`variant-${v.id}-group-toggle-${g.id}`}
              title={enabled ? 'Aktiv (klikləyib bağla)' : 'Deaktiv (klikləyib aç)'}
              style={enabled ? {
                borderColor: color,
                color,
                boxShadow: `0 0 14px ${color}55, inset 0 0 12px ${color}22`,
              } : undefined}
            >
              <span
                className="conn-group-dot"
                style={enabled ? { background: color, boxShadow: `0 0 8px ${color}` } : undefined}
              />
              <span className="conn-group-name">{g.name}</span>
              {g.connections && g.connections.length > 0 && (
                <span className="conn-group-count">{g.connections.length}</span>
              )}
            </button>
          );
        })}
      </div>
    ) : null;

    // Include date_source='sum' cells in both columns (they display a single
    // combined value the user wants visible).
    const filterVariantCellsFor = (which) => (c.grid_cells || []).filter((gc) => {
      const ds = gc.date_source || 'primary';
      return ds === 'both' || ds === 'sum' || ds === which;
    });
    const gridBlock = (c.grid_cells || []).length > 0 ? (
      (compatEnabled && v.compat_enabled && results2 && birthDate2) ? (
        <div style={{ width: '100%', maxWidth: boxWidth, marginTop: 12 }}>
          <div className="dual-matrix-wrap">
            <div className="dual-matrix-col" data-testid={`variant-grid-date-1-${v.id}`}>
              <div className="dual-matrix-heading">1-ci tarix: {birthDate?.day}.{birthDate?.month}.{birthDate?.year}</div>
              <GridCellsRenderer
                cells={filterVariantCellsFor('primary')}
                results={results}
                day={day}
                month={month}
                year={year}
                results2={results2}
                day2={day2}
                month2={month2}
                year2={year2}
                resultsSum={sumResults}
                gridBgImage={c.grid_bg_image || ''}
                gridTitle={c.grid_title || ''}
                popupsEnabled={c.grid_popups_enabled !== false}
              />
            </div>
            <div className="dual-matrix-col" data-testid={`variant-grid-date-2-${v.id}`}>
              <div className="dual-matrix-heading">2-ci tarix: {birthDate2?.day}.{birthDate2?.month}.{birthDate2?.year}</div>
              <GridCellsRenderer
                cells={filterVariantCellsFor('secondary')}
                results={results2}
                day={day2}
                month={month2}
                year={year2}
                results2={results}
                day2={day}
                month2={month}
                year2={year}
                resultsSum={sumResults}
                gridBgImage={c.grid_bg_image || ''}
                gridTitle={c.grid_title || ''}
                popupsEnabled={c.grid_popups_enabled !== false}
              />
            </div>
          </div>
        </div>
      ) : (
        <div style={{ width: '100%', maxWidth: boxWidth, marginTop: 12 }}>
          <GridCellsRenderer
            cells={c.grid_cells}
            results={results}
            day={day}
            month={month}
            year={year}
            results2={compatEnabled ? results2 : null}
            day2={day2}
            month2={month2}
            year2={year2}
            resultsSum={sumResults}
            gridBgImage={c.grid_bg_image || ''}
            gridTitle={c.grid_title || ''}
          />
        </div>
      )
    ) : null;

    return (
      <div
        key={v.id}
        className="dynamic-section"
        data-testid={`variant-section-${v.id}`}
        style={{ position: 'relative' }}
      >
        {v.title && (
          <h2
            className="dynamic-section-title"
            style={{ fontSize: `${1.4 * titleScale}rem` }}
          >
            {v.title}
          </h2>
        )}

        {/* Groups can appear ABOVE the matrix (per-variant admin setting). */}
        {!v.hide_groups && groupsPos === 'above' && groupsBar}

        {/* Matrix + variant-scoped background wrapper.  The bg only covers this
            box (not the whole section), so grid cells below stay clean. */}
        {!v.hide_matrix && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: boxWidth,
            overflow: 'hidden',
            borderRadius: 8,
          }}
        >
          {variantLayers.length > 0 && (
            <div
              aria-hidden="true"
              data-testid={`variant-bg-${v.id}`}
              style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                overflow: 'hidden',
                zIndex: 0,
              }}
            >
              {variantLayers.map((layer, i) => {
                const url = `${BACKEND_URL}/api/uploads/${layer.filename}`;
                const sc = layer.scale ?? 1;
                const ox = layer.offset_x ?? 0;
                const oy = layer.offset_y ?? 0;
                const op = layer.opacity ?? 1;
                const fit = layer.fit_mode || 'cover';
                let objectFit = 'cover';
                if (fit === 'contain') objectFit = 'contain';
                else if (fit === 'stretch') objectFit = 'fill';
                else if (fit === 'natural') objectFit = 'none';
                return (
                  <img
                    key={`vbg-${v.id}-${i}-${layer.filename}`}
                    src={url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      width: '100%',
                      height: '100%',
                      objectFit,
                      transform: `translateX(-50%) translate(${ox}px, ${oy}px) scale(${sc})`,
                      opacity: op,
                      transformOrigin: 'center center',
                      willChange: 'transform',
                    }}
                  />
                );
              })}
            </div>
          )}
          <div style={{ position: 'relative', zIndex: 1 }}>
            {(compatEnabled && v.compat_enabled && results2 && birthDate2) ? (
              <div className="dual-matrix-wrap">
                <div className="dual-matrix-col" data-testid={`variant-matrix-date-1-${v.id}`}>
                  <div className="dual-matrix-heading">1-ci tarix: {birthDate?.day}.{birthDate?.month}.{birthDate?.year}</div>
                  <KarmaMatrix
                    results={results}
                    connections={effectiveConnections}
                    nodeLabels={c.node_labels || {}}
                    nodePositions={c.node_positions || {}}
                    nodeColors={c.node_colors || {}}
                    nodeTextColors={c.node_text_colors || {}}
                    nodeSizes={c.node_sizes || {}}
                    nodeShapes={c.node_shapes || {}}
                    globalSphereSize={c.global_sphere_size || 44}
                    backgroundImage={null}
                    backgroundLayers={[]}
                    backgroundFullscreen={false}
                    paradiseMode={c.paradise_mode}
                    hiddenSpheres={c.hidden_spheres || []}
                    hiddenValues={c.hidden_values || []}
                    connectionColorMap={mergedColorMap}
                    connectionSpeed={c.connection_speed}
                    connectionStyle={c.connection_style}
                    gates={c.gates || {}}
                    gateValues={c.gate_values || {}}
                    customNodes={(c.custom_nodes || []).filter((cn) => {
                      const ds = cn.date_source || 'primary';
                      return ds === 'both' || ds === 'primary';
                    })}
                    evaluateFormula={evaluateFormula}
                    sumValues={sumResults}
                    showSum={!!sumResults}
                  />
                </div>
                <div className="dual-matrix-col" data-testid={`variant-matrix-date-2-${v.id}`}>
                  <div className="dual-matrix-heading">2-ci tarix: {birthDate2?.day}.{birthDate2?.month}.{birthDate2?.year}</div>
                  <KarmaMatrix
                    results={results2}
                    connections={effectiveConnections}
                    nodeLabels={c.node_labels || {}}
                    nodePositions={c.node_positions || {}}
                    nodeColors={c.node_colors || {}}
                    nodeTextColors={c.node_text_colors || {}}
                    nodeSizes={c.node_sizes || {}}
                    nodeShapes={c.node_shapes || {}}
                    globalSphereSize={c.global_sphere_size || 44}
                    backgroundImage={null}
                    backgroundLayers={[]}
                    backgroundFullscreen={false}
                    paradiseMode={c.paradise_mode}
                    hiddenSpheres={c.hidden_spheres || []}
                    hiddenValues={c.hidden_values || []}
                    connectionColorMap={mergedColorMap}
                    connectionSpeed={c.connection_speed}
                    connectionStyle={c.connection_style}
                    gates={c.gates || {}}
                    gateValues={c.gate_values || {}}
                    customNodes={(c.custom_nodes || []).filter((cn) => {
                      const ds = cn.date_source || 'primary';
                      return ds === 'both' || ds === 'secondary';
                    })}
                    evaluateFormula={evaluateFormula}
                    sumValues={sumResults}
                    showSum={!!sumResults}
                  />
                </div>
              </div>
            ) : (
              <KarmaMatrix
                results={results}
                connections={effectiveConnections}
                nodeLabels={c.node_labels || {}}
                nodePositions={c.node_positions || {}}
                nodeColors={c.node_colors || {}}
                nodeTextColors={c.node_text_colors || {}}
                nodeSizes={c.node_sizes || {}}
                nodeShapes={c.node_shapes || {}}
                globalSphereSize={c.global_sphere_size || 44}
                /* KarmaMatrix's OWN bg rendering is disabled — variant bg is drawn
                   by the wrapper above so it can visibly enlarge beyond the SVG. */
                backgroundImage={null}
                backgroundLayers={[]}
                backgroundFullscreen={true}
                paradiseMode={c.paradise_mode}
                hiddenSpheres={c.hidden_spheres || []}
                hiddenValues={c.hidden_values || []}
                connectionColorMap={mergedColorMap}
                connectionSpeed={c.connection_speed}
                connectionStyle={c.connection_style}
                gates={c.gates || {}}
                gateValues={c.gate_values || {}}
                customNodes={(c.custom_nodes || []).filter((cn) => {
                  // In single-date mode (either compat off or this variant's
                  // compat_enabled is false), drop nodes explicitly tagged
                  // 'secondary' so only date-1 nodes render.
                  const ds = cn.date_source || 'primary';
                  return ds !== 'secondary';
                })}
                evaluateFormula={evaluateFormula}
              />
            )}
          </div>
        </div>
        )}

        {/* Groups sit right under the matrix (variant admin choice). */}
        {!v.hide_groups && groupsPos === 'below-matrix' && groupsBar}

        {/* Grid cells appear right below the matrix — layout order is
            controlled by `groups_position` (above / below-matrix / below-grid). */}
        {!v.hide_grid && gridBlock}

        {c.energy_calc?.enabled && (
          <div style={{ width: '100%', maxWidth: boxWidth, marginTop: 16 }} data-testid={`variant-energy-${v.id}`}>
            <EnergyCalculator config={c.energy_calc} birthDate={birthDate} testIdPrefix={`energy-var-${v.id}`} />
          </div>
        )}

        {/* Groups sit at the very bottom (default, variant admin choice). */}
        {!v.hide_groups && groupsPos === 'below-grid' && groupsBar}
      </div>
    );
  };

  if (layout === 'tabs') {
    return (
      <div
        className="variants-tabs-wrap"
        data-testid="variants-section-tabs"
        style={{ marginTop: 24, width: '100%', maxWidth: 'min(1000px, 96vw)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center',
          marginBottom: 18, padding: '10px', background: 'rgba(168,85,247,0.06)',
          borderRadius: 10, border: '1px solid rgba(168,85,247,0.2)',
        }}>
          {variants.map((v) => (
            <button key={v.id}
              onClick={() => setActiveTab(v.id)}
              data-testid={`variant-tab-${v.id}`}
              style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: activeTab === v.id ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${activeTab === v.id ? 'rgba(168,85,247,0.7)' : 'rgba(255,255,255,0.15)'}`,
                color: 'white', cursor: 'pointer',
                fontFamily: "'Cinzel', serif",
              }}>
              {v.title}
            </button>
          ))}
        </div>
        {variants.filter((v) => v.id === activeTab).map(renderOne)}
      </div>
    );
  }

  // stacked
  return (
    <div
      className="variants-stacked-wrap"
      data-testid="variants-section-stacked"
      style={{ marginTop: 24, width: '100%', maxWidth: 'min(1000px, 96vw)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}
    >
      {variants.map(renderOne)}
    </div>
  );
};

export default VariantsSection;
